import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Plus, 
  Search, 
  FolderKanban,
  Mail,
  Phone,
  Building,
  MoreHorizontal,
  Pencil,
  Trash2,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Rocket,
  Calendar,
  Eye,
  Clock,
  Loader2,
  X,
  AlertTriangle,
  Eraser,
  Copy,
  Crown,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

// Status badge colors
const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-gray-500/10 text-gray-400 border-gray-500/30' },
  in_progress: { label: 'En Progreso', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30' },
  active: { label: 'Activo', color: 'bg-green-500/10 text-green-400 border-green-500/30' },
  completed: { label: 'Completado', color: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  paused: { label: 'Pausado', color: 'bg-orange-500/10 text-orange-400 border-orange-500/30' },
};

// Project Card with expandable funnels
const ProjectCard = ({ project, onEdit, onDelete, onDuplicate, api, onFunnelDeleted }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [funnels, setFunnels] = useState([]);
  const [loadingFunnels, setLoadingFunnels] = useState(false);
  const [deletingFunnel, setDeletingFunnel] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [funnelToDelete, setFunnelToDelete] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const navigate = useNavigate();

  // Check if project has optional contact info
  const hasContactInfo = project.email || project.phone;

  const fetchProjectFunnels = async () => {
    if (funnels.length > 0 || project.funnels_count === 0) {
      setIsExpanded(!isExpanded);
      return;
    }
    
    setLoadingFunnels(true);
    try {
      const response = await api.get(`/clients/${project.id}/funnels`);
      setFunnels(response.data || []);
      setIsExpanded(true);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error loading funnels:', error);
      toast.error('Error al cargar los funnels');
    } finally {
      setLoadingFunnels(false);
    }
  };

  const handleCardClick = () => {
    if (project.funnels_count > 0) {
      fetchProjectFunnels();
    }
  };

  const handleDeleteFunnel = async () => {
    if (!funnelToDelete) return;
    
    setDeletingFunnel(funnelToDelete.id);
    try {
      await api.delete(`/funnels/${funnelToDelete.id}`);
      setFunnels(prev => prev.filter(f => f.id !== funnelToDelete.id));
      
      if (onFunnelDeleted) {
        onFunnelDeleted(project.id);
      }
      
      toast.success('Funnel eliminado', {
        description: 'Todos los assets asociados han sido eliminados'
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error deleting funnel:', error);
      toast.error('Error al eliminar el funnel');
    } finally {
      setDeletingFunnel(null);
      setShowDeleteModal(false);
      setFunnelToDelete(null);
    }
  };

  const handleDuplicateProject = async (e) => {
    e.stopPropagation();
    setDuplicating(true);
    try {
      await onDuplicate(project);
    } finally {
      setDuplicating(false);
    }
  };

  const openDeleteModal = (funnel, e) => {
    e.stopPropagation();
    setFunnelToDelete(funnel);
    setShowDeleteModal(true);
  };

  return (
    <div
      className={`bg-surface border rounded-xl overflow-hidden transition-all ${
        isExpanded ? 'border-brand/50 ring-1 ring-brand/20' : 'border-border-subtle'
      }`}
      data-testid={`project-card-${project.id}`}
    >
      {/* Project Header */}
      <div 
        className="p-5 cursor-pointer hover:bg-app-bg/30 transition-colors"
        onClick={handleCardClick}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand/20 to-purple-500/20 flex items-center justify-center">
              <span className="text-brand-500 font-bold text-lg">
                {project.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="font-semibold text-text-main text-lg">{project.name}</h3>
              {project.company && (
                <p className="text-text-secondary text-sm flex items-center gap-1.5">
                  <Building className="w-3.5 h-3.5" />
                  {project.company}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-surface border-border-subtle">
                <DropdownMenuItem onClick={() => onEdit(project)}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDuplicateProject} disabled={duplicating}>
                  {duplicating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Copy className="w-4 h-4 mr-2" />
                  )}
                  Duplicar Proyecto
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(project.id)}
                  className="text-red-400 focus:text-red-400"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Contact info - only shown if present */}
        {hasContactInfo && (
          <div className="flex items-center gap-4 text-sm text-text-muted mb-3">
            {project.email && (
              <span className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />
                {project.email}
              </span>
            )}
            {project.phone && (
              <span className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" />
                {project.phone}
              </span>
            )}
          </div>
        )}

        <div className={`flex items-center justify-between pt-3 ${hasContactInfo ? '' : 'mt-2'} border-t border-border-subtle`}>
          <button 
            className={`flex items-center gap-2 text-sm font-medium transition-colors ${
              project.funnels_count > 0 ? 'text-brand-500 hover:text-brand-hover' : 'text-text-muted'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
          >
            {loadingFunnels ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            <FolderOpen className="w-4 h-4" />
            <span>{project.funnels_count} {project.funnels_count === 1 ? 'funnel' : 'funnels'}</span>
          </button>
          <span className="text-text-muted text-xs flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(project.created_at).toLocaleDateString('es-ES')}
          </span>
        </div>
      </div>

      {/* Expanded Funnels List */}
      {isExpanded && (
        <div className="border-t border-border-subtle bg-app-bg/30">
          {funnels.length > 0 ? (
            <div className="divide-y divide-border-subtle">
              {funnels.map((funnel) => {
                const statusConfig = STATUS_CONFIG[funnel.status] || STATUS_CONFIG.draft;
                return (
                  <div 
                    key={funnel.id}
                    className="p-4 hover:bg-surface/50 transition-colors cursor-pointer flex items-center justify-between group"
                    onClick={() => navigate(`/funnel/${funnel.id}`)}
                    data-testid={`funnel-row-${funnel.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                        <Rocket className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h4 className="font-medium text-text-main text-sm group-hover:text-brand-500 transition-colors">
                          {funnel.name || 'Sin nombre'}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-text-muted">
                          <span>{funnel.strategy_id || 'Sin estrategia'}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(funnel.updated_at || funnel.created_at).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      <span className={`px-2 py-1 rounded-full text-xs border ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                      
                      {/* Funnel Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-surface border-border-subtle">
                          <DropdownMenuItem onClick={() => navigate(`/funnel/${funnel.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Ver Funnel
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => openDeleteModal(funnel, e)}
                            className="text-red-400 focus:text-red-400"
                            disabled={deletingFunnel === funnel.id}
                          >
                            {deletingFunnel === funnel.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Eliminar Funnel
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : project.funnels_count === 0 ? (
            <div className="p-6 text-center">
              <Rocket className="w-8 h-8 text-text-muted mx-auto mb-2 opacity-50" />
              <p className="text-text-muted text-sm">Este proyecto no tiene funnels aún</p>
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/funnel/new');
                }}
                className="mt-3 bg-brand/20 text-brand-500 hover:bg-brand/30"
              >
                <Plus className="w-3 h-3 mr-1.5" />
                Crear Funnel
              </Button>
            </div>
          ) : (
            <div className="p-6 text-center">
              <Loader2 className="w-6 h-6 text-brand-500 animate-spin mx-auto" />
            </div>
          )}
        </div>
      )}

      {/* Delete Funnel Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-surface border-border-subtle">
          <DialogHeader>
            <DialogTitle className="text-text-main font-display flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Eliminar Funnel
            </DialogTitle>
            <DialogDescription className="text-text-secondary">
              ¿Estás seguro de que quieres eliminar el funnel "{funnelToDelete?.name || 'Sin nombre'}"?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">
                <strong>Esta acción no se puede deshacer.</strong> Se eliminarán:
              </p>
              <ul className="mt-2 text-xs text-red-400/80 space-y-1">
                <li>• Todos los copies generados</li>
                <li>• Todas las imágenes y vídeos</li>
                <li>• Todas las secuencias de email/WhatsApp</li>
                <li>• Todo el historial de versiones</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              className="border-border-subtle text-text-secondary"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteFunnel}
              disabled={deletingFunnel}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingFunnel ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Eliminar Funnel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Projects Page
const Projects = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [cleaningDrafts, setCleaningDrafts] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [projectLimitInfo, setProjectLimitInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    notes: '',
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/clients');
      const projectsWithCount = (response.data || []).map(project => ({
        ...project,
        funnels_count: typeof project.funnels_count === 'number' ? project.funnels_count : 0
      }));
      setProjects(projectsWithCount);
    } catch (error) {
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  const handleFunnelDeleted = (projectId) => {
    setProjects(prev => prev.map(project => {
      if (project.id === projectId) {
        return {
          ...project,
          funnels_count: Math.max(0, (project.funnels_count || 1) - 1)
        };
      }
      return project;
    }));
  };

  const handleCleanupDrafts = async () => {
    setCleaningDrafts(true);
    try {
      const response = await api.delete('/funnels/cleanup/drafts');
      const { deleted_count, deleted_funnels } = response.data;
      
      if (deleted_count > 0) {
        toast.success(`${deleted_count} borradores eliminados`, {
          description: deleted_funnels.slice(0, 3).join(', ') + (deleted_funnels.length > 3 ? '...' : '')
        });
        fetchProjects();
      } else {
        toast.info('No hay borradores vacíos para eliminar');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error cleaning drafts:', error);
      toast.error('Error al limpiar borradores');
    } finally {
      setCleaningDrafts(false);
      setShowCleanupModal(false);
    }
  };

  const handleDuplicateProject = async (project) => {
    try {
      const response = await api.post(`/clients/${project.id}/duplicate`);
      toast.success('Proyecto duplicado', {
        description: `"${response.data.name}" creado correctamente`
      });
      // Add the new project to the list
      setProjects(prev => [response.data, ...prev]);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error duplicating project:', error);
      toast.error('Error al duplicar el proyecto');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      if (editingProject) {
        await api.put(`/clients/${editingProject.id}`, formData);
        toast.success('Proyecto actualizado');
      } else {
        // Check project limit before creating
        const limitCheck = await api.post('/billing/check-project-limit');
        if (!limitCheck.data.can_create) {
          setProjectLimitInfo(limitCheck.data);
          setShowUpgradeModal(true);
          return;
        }
        
        await api.post('/clients', formData);
        toast.success('Proyecto creado');
      }
      setIsModalOpen(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al guardar proyecto';
      toast.error(errorMsg);
      console.error('Save project error:', error.response?.data || error);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('¿Estás seguro de eliminar este proyecto y todos sus funnels?')) return;
    
    try {
      await api.delete(`/clients/${projectId}`);
      toast.success('Proyecto eliminado');
      fetchProjects();
    } catch (error) {
      toast.error('Error al eliminar proyecto');
    }
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      email: project.email || '',
      company: project.company || '',
      phone: project.phone || '',
      notes: project.notes || '',
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingProject(null);
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      notes: '',
    });
  };

  const filteredProjects = projects.filter(
    (project) =>
      project.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalFunnels = projects.reduce((acc, p) => acc + (p.funnels_count || 0), 0);

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="projects-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold text-text-main">Mis Proyectos</h1>
            <p className="text-text-secondary mt-1" data-testid="projects-stats">
              {projects.length} proyectos • {totalFunnels} funnels en total
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowCleanupModal(true)}
              className="border-border-subtle text-text-secondary hover:text-red-400 hover:border-red-400/50"
              data-testid="cleanup-drafts-btn"
            >
              <Eraser className="w-4 h-4 mr-2" />
              Limpiar Borradores
            </Button>
            
            <Button
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
              className="bg-brand-500 hover:bg-brand-hover text-white shadow-glow-brand"
              data-testid="add-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Proyecto
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
          <Input
            placeholder="Buscar proyectos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-surface border-border-subtle focus:border-brand-500 text-text-main placeholder:text-text-muted"
            data-testid="search-projects"
          />
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="bg-surface border border-border-subtle rounded-xl p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-100 to-violet-100 dark:from-brand-900/30 dark:to-violet-900/30 flex items-center justify-center mx-auto mb-4">
              <FolderKanban className="w-8 h-8 text-brand-500" />
            </div>
            <h3 className="text-lg font-semibold text-text-main mb-2">
              {searchQuery ? 'No se encontraron proyectos' : 'No hay proyectos aún'}
            </h3>
            <p className="text-text-secondary mb-2">
              {searchQuery
                ? 'Intenta con otro término de búsqueda'
                : 'Crea tu primer proyecto para organizar tus funnels por cliente'}
            </p>
            {!searchQuery && (
              <>
                <p className="text-sm text-brand-500 font-medium mb-6">
                  Tu primer funnel completo en ~5 minutos
                </p>
                <Button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-gradient-to-r from-brand-500 to-violet-500 hover:from-brand-600 hover:to-violet-600 text-white shadow-lg"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Proyecto
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                api={api}
                onEdit={openEditModal}
                onDelete={handleDelete}
                onDuplicate={handleDuplicateProject}
                onFunnelDeleted={handleFunnelDeleted}
              />
            ))}
          </div>
        )}

        {/* Create/Edit Project Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="bg-surface border-border-subtle">
            <DialogHeader>
              <DialogTitle className="text-text-main font-display">
                {editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-text-secondary">Nombre del Proyecto *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Mi proyecto de marketing"
                  className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted"
                  data-testid="project-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-text-secondary">Empresa/Marca (opcional)</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Nombre de la empresa o marca"
                  className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted"
                  data-testid="project-company-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-text-secondary">Email (opcional)</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@ejemplo.com"
                    className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted"
                    data-testid="project-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-text-secondary">Teléfono (opcional)</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+34 600 000 000"
                    className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted"
                    data-testid="project-phone-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="border-border-subtle text-text-secondary"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-brand-500 hover:bg-brand-hover text-white"
                  data-testid="save-project-btn"
                >
                  {editingProject ? 'Guardar Cambios' : 'Crear Proyecto'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Cleanup Drafts Modal */}
        <Dialog open={showCleanupModal} onOpenChange={setShowCleanupModal}>
          <DialogContent className="bg-surface border-border-subtle">
            <DialogHeader>
              <DialogTitle className="text-text-main font-display flex items-center gap-2">
                <Eraser className="w-5 h-5 text-amber-400" />
                Limpiar Borradores
              </DialogTitle>
              <DialogDescription className="text-text-secondary">
                Esta acción eliminará todos los funnels en estado "Borrador" que no tengan:
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-center gap-2">
                  <X className="w-4 h-4 text-red-400" />
                  Estrategia seleccionada
                </li>
                <li className="flex items-center gap-2">
                  <X className="w-4 h-4 text-red-400" />
                  Contenido aprobado
                </li>
              </ul>
              <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-sm text-amber-400">
                  Los funnels con contenido aprobado <strong>no serán eliminados</strong>.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCleanupModal(false)}
                className="border-border-subtle text-text-secondary"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCleanupDrafts}
                disabled={cleaningDrafts}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {cleaningDrafts ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eraser className="w-4 h-4 mr-2" />
                )}
                Limpiar Borradores
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upgrade Plan Modal - Triggered when project limit reached */}
        <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
          <DialogContent className="bg-surface border-border-subtle">
            <DialogHeader>
              <DialogTitle className="text-text-main font-display flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                Límite de Proyectos Alcanzado
              </DialogTitle>
              <DialogDescription className="text-text-secondary">
                Has alcanzado el límite de proyectos de tu plan actual.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {projectLimitInfo && (
                <div className="space-y-4">
                  <div className="p-4 bg-app-bg rounded-xl border border-border-subtle">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-text-muted">Proyectos actuales</span>
                      <span className="text-lg font-bold text-text-main">
                        {projectLimitInfo.current_count} / {projectLimitInfo.limit}
                      </span>
                    </div>
                    <div className="h-2 bg-surface rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-red-500 rounded-full"
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  
                  <div className="p-4 bg-brand/10 border border-brand/30 rounded-xl">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-brand/20 flex items-center justify-center shrink-0">
                        <Zap className="w-5 h-5 text-brand-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-brand-500">Upgrade al Plan Agencia</p>
                        <p className="text-sm text-text-secondary mt-1">
                          Obtén <strong>proyectos ilimitados</strong>, 5.000 créditos/mes y soporte prioritario.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowUpgradeModal(false)}
                className="border-border-subtle text-text-secondary"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/billing');
                }}
                className="bg-brand-500 hover:bg-brand-hover text-white"
              >
                <Crown className="w-4 h-4 mr-2" />
                Ver Planes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default Projects;
